<main class="page">
  <button
    matButton="filled"
    color="primary"
    (click)="backToDashboard()"
    matTooltip="Back to dashboard"
    class="dashboard-button"
  >
    <mat-icon>keyboard_backspace</mat-icon>
    Back to dashboard
  </button>
  <header>
    <div>
      <p class="eyebrow">Smart Fix administration</p>
      <h1>Conversation quality overview</h1>
      <p>Review flagged conversations and evaluation findings in one place.</p>
    </div>
  </header>
  @if (!isResultsRoute()) {
    <aside class="po-guide" aria-label="How to use this page">
      <mat-icon aria-hidden="true">tips_and_updates</mat-icon>
      <div>
        <ol>
          <li>Daily alerts (scroll down to find evaluations)</li>
        </ol>
      </div>
    </aside>
    <section class="primary-content">
      <div class="grid">
        <app-weekly-alerts-widget
          title="Text conversations"
          [findings]="agentWeeklyAlerts"
          [loading]="agentWeeklyLoading"
          [error]="agentWeeklyError"
          [updatingReviewKeys]="updatingReviewKeys"
          (weekSelected)="loadWeeklyAlerts('agent-llm', $event)"
          (dateRangeChange)="loadWeeklyAlertsWithRange('agent-llm', $event)"
          (reviewStatusChange)="updateReviewStatus('agent-llm', $event)"
        ></app-weekly-alerts-widget>
        <app-weekly-alerts-widget
          title="Voice conversations"
          [findings]="voiceWeeklyAlerts"
          [loading]="voiceWeeklyLoading"
          [error]="voiceWeeklyError"
          [updatingReviewKeys]="updatingReviewKeys"
          (weekSelected)="loadWeeklyAlerts('voice2voice', $event)"
          (dateRangeChange)="loadWeeklyAlertsWithRange('voice2voice', $event)"
          (reviewStatusChange)="updateReviewStatus('voice2voice', $event)"
        ></app-weekly-alerts-widget>
      </div>

      <div class="section-heading evaluations-heading">
        <h2>Evaluation runs</h2>
        <p>Select a completed run to review its quality scores.</p>
      </div>
      <div class="grid">
        <app-evaluation-runs-widget
          title="Text conversation evaluations"
          [runs]="agentEvaluationRuns"
          [loading]="agentEvaluationRunsLoading"
          [error]="agentEvaluationRunsError"
          (selectRun)="selectEvaluationRun('agent-llm', $event)"
        ></app-evaluation-runs-widget>
        <app-evaluation-runs-widget
          title="Voice conversation evaluations"
          [runs]="voiceEvaluationRuns"
          [loading]="voiceEvaluationRunsLoading"
          [error]="voiceEvaluationRunsError"
          (selectRun)="selectEvaluationRun('voice2voice', $event)"
        ></app-evaluation-runs-widget>
      </div>

      <section class="run-quality-summary">
        <div class="run-quality-summary-intro">
          <h2>Average transcript quality score</h2>
          <p>Average score of the evaluation runs</p>
        </div>
        <div class="run-quality-summary-cards">
          <div class="run-quality-summary-card">
            <small>Text-based conversations (agent llm) </small>
            <strong>
              @if (agentAverageQualityScore !== null) {
                {{ agentAverageQualityScore | number: '1.1-1' }}%
              } @else {
                —
              }
            </strong>
          </div>
          <div class="run-quality-summary-card">
            <small>Voice 2 Voice conversations </small>
            <strong>
              @if (voiceAverageQualityScore !== null) {
                {{ voiceAverageQualityScore | number: '1.1-1' }}%
              } @else {
                —
              }
            </strong>
          </div>
        </div>
      </section>

      <section
        class="secondary-content"
        aria-label="Detailed analysis and compliance data"
      >
        <details>
          <summary>Detailed alert register and filters</summary>
          <p>
            Use these tables when you need to search, export or audit all
            flagged findings.
          </p>
          <div class="grid">
            <app-compliance-findings-widget
              title="Text compliance findings"
              [findings]="agentFindings"
              [loading]="agentComplianceLoading"
              [error]="agentComplianceError"
              [updatingReviewKeys]="updatingReviewKeys"
              (apply)="applyCompliance('agent-llm', $event)"
              (clear)="clearCompliance('agent-llm')"
              (refresh)="refreshCompliance('agent-llm')"
              (reviewStatusChange)="updateReviewStatus('agent-llm', $event)"
            ></app-compliance-findings-widget>
            <app-compliance-findings-widget
              title="Voice compliance findings"
              [voice]="true"
              [findings]="voiceFindings"
              [loading]="voiceComplianceLoading"
              [error]="voiceComplianceError"
              [updatingReviewKeys]="updatingReviewKeys"
              (apply)="applyCompliance('voice2voice', $event)"
              (clear)="clearCompliance('voice2voice')"
              (refresh)="refreshCompliance('voice2voice')"
              (reviewStatusChange)="updateReviewStatus('voice2voice', $event)"
            ></app-compliance-findings-widget>
          </div>
        </details>
        <details>
          <summary>Transcript analysis activity</summary>
          <p>
            Technical processing history for troubleshooting and audit
            follow-up.
          </p>
          <div class="grid">
            <app-transcript-analysis-runs-widget
              title="Text transcript analyses"
              [runs]="agentAnalysisRuns"
              [count]="agentAnalysisRunCount"
              [responseDays]="agentAnalysisRunDays"
              [loading]="agentRunsLoading"
              [error]="agentRunsError"
              (query)="loadAnalysisRuns('agent-llm', $event)"
            ></app-transcript-analysis-runs-widget>
            <app-transcript-analysis-runs-widget
              title="Voice transcript analyses"
              [runs]="voiceAnalysisRuns"
              [count]="voiceAnalysisRunCount"
              [responseDays]="voiceAnalysisRunDays"
              [loading]="voiceRunsLoading"
              [error]="voiceRunsError"
              (query)="loadAnalysisRuns('voice2voice', $event)"
            ></app-transcript-analysis-runs-widget>
          </div>
        </details>
      </section>
    </section>
  } @else {
    <div class="grid">
      <div class="back-navigation">
        <button mat-flat-button color="primary" (click)="goBackToMain()">
          <mat-icon>arrow_back</mat-icon>
          Back to Runs List
        </button>
      </div>
      <app-evaluation-results-widget
        title="Text Conversation Evaluation (Agent-LLM)"
        placeholder="e.g. 2026-09-07 (Mondays)"
        [results]="agentResults"
        [loading]="agentLoading"
        [error]="agentError"
        [(runDate)]="agentRunDate"
        [(group)]="agentGroup"
        [(metric)]="agentMetric"
        [groups]="groups"
        [metrics]="metrics"
        (loadResults)="loadEvaluation('agent-llm')"
        (clear)="clearEvaluation('agent-llm')"
      ></app-evaluation-results-widget>
      <app-evaluation-results-widget
        title="Voice-to-Voice Evaluation (Nova Sonic)"
        placeholder="e.g. 2026-09-07 (Mondays)"
        [results]="voiceResults"
        [loading]="voiceLoading"
        [error]="voiceError"
        [(runDate)]="voiceRunDate"
        [(group)]="voiceGroup"
        [(metric)]="voiceMetric"
        [groups]="voiceGroups"
        [metrics]="metrics"
        (loadResults)="loadEvaluation('voice2voice')"
        (clear)="clearEvaluation('voice2voice')"
      ></app-evaluation-results-widget>
      <details class="full-width">
        <summary>Detailed compliance findings</summary>
        <p>
          Search and audit the full alert register when you need more detail.
        </p>
        <div class="grid">
          <app-compliance-findings-widget
            title="Text compliance findings"
            [findings]="agentFindings"
            [loading]="agentComplianceLoading"
            [error]="agentComplianceError"
            (apply)="applyCompliance('agent-llm', $event)"
            (clear)="clearCompliance('agent-llm')"
            (refresh)="refreshCompliance('agent-llm')"
            (reviewStatusChange)="updateReviewStatus('agent-llm', $event)"
          ></app-compliance-findings-widget>
          <app-compliance-findings-widget
            title="Voice compliance findings"
            [voice]="true"
            [findings]="voiceFindings"
            [loading]="voiceComplianceLoading"
            [error]="voiceComplianceError"
            (apply)="applyCompliance('voice2voice', $event)"
            (clear)="clearCompliance('voice2voice')"
            (refresh)="refreshCompliance('voice2voice')"
            (reviewStatusChange)="updateReviewStatus('voice2voice', $event)"
          ></app-compliance-findings-widget>
        </div>
      </details>
    </div>
  }
</main>
